/** Every entity the live mounts hold, grouped by address. Read-only: the editor never writes state. */

import { useState } from "react";

import { Accordion, CopyField, Field, Input, Select, Stack, StatusBadge } from "@/components/ui";

import { Mono, Muted } from "../../components/Text";
import type { SpaceActivities } from "../../spaces/lib/queries";

import {
  byAddress,
  matches,
  stateRows,
  tagsIn,
  typesIn,
  type AttributeReading,
  type EffectReading,
  type StateFilter,
  type StateRow,
} from "../lib/stateRows";
import { useNow } from "../lib/useNow";

import { LiveMounts, type MountReading } from "./LiveMounts";
import { Nothing, Section } from "./Section";

function bound(min: number | null, max: number | null): string {
  if (min === null && max === null) return "";
  return ` [${min === null ? "" : String(min)}..${max === null ? "" : String(max)}]`;
}

function AttributeReadout({ attribute }: { readonly attribute: AttributeReading }) {
  return (
    <span className="cx-node-name">
      {String(attribute.current)}
      {attribute.current === attribute.base ? null : (
        <Muted small>{` base ${String(attribute.base)}`}</Muted>
      )}
      <Muted small>{bound(attribute.min, attribute.max)}</Muted>
    </span>
  );
}

function AttributeItem({ attribute }: { readonly attribute: AttributeReading }) {
  if (attribute.modifiers.length === 0) {
    return (
      <div className="cx-row">
        <div className="cx-row-id">
          <Mono>{attribute.name}</Mono>
        </div>
        <AttributeReadout attribute={attribute} />
      </div>
    );
  }

  return (
    <Accordion
      items={[
        {
          id: attribute.name,
          title: <Mono>{attribute.name}</Mono>,
          meta: <AttributeReadout attribute={attribute} />,
          content: (
            <Stack gap={1}>
              {attribute.modifiers.map((one, at) => (
                <div className="cx-row" key={`${one.source}-${String(at)}`}>
                  <div className="cx-row-id">
                    <Mono>{`${one.op} ${String(one.magnitude)}`}</Mono>
                  </div>
                  <Muted small>{one.source}</Muted>
                </div>
              ))}
            </Stack>
          ),
        },
      ]}
    />
  );
}

function EffectItem({ effect, now }: { readonly effect: EffectReading; readonly now: number }) {
  const remaining =
    effect.expiresAt === null ? null : Math.max(0, Math.ceil(effect.expiresAt - now));

  const detail = [
    effect.foreign ? `applied by ${effect.appliedBy}` : null,
    effect.stacks > 1 ? `x${String(effect.stacks)}` : null,
    remaining === null ? null : `${String(remaining)}s left`,
  ].filter((one): one is string => one !== null);

  return (
    <div className="cx-row">
      <div className="cx-row-id">
        <div>
          <div className="cx-node-name">
            <Mono>{effect.name}</Mono>
          </div>
          {detail.length === 0 ? null : <Muted small>{detail.join("  ")}</Muted>}
        </div>
      </div>
      {effect.suspended ? <StatusBadge tone="warning">Suspended</StatusBadge> : null}
    </div>
  );
}

function entityItem(row: StateRow, now: number) {
  return {
    id: row.key,
    title: <Mono>{row.id}</Mono>,
    meta: (
      <>
        <Muted small>{row.type}</Muted>
        {row.addressed ? null : <StatusBadge tone="warning">no address</StatusBadge>}
      </>
    ),
    content: (
      <Stack gap={3}>
        {row.attributes.length === 0 ? null : (
          <Stack gap={1}>
            {row.attributes.map((attribute) => (
              <AttributeItem key={attribute.name} attribute={attribute} />
            ))}
          </Stack>
        )}
        {row.text.length === 0 ? null : (
          <Stack gap={1}>
            {row.text.map((one) => (
              <div className="cx-row" key={one.name}>
                <div className="cx-row-id">
                  <Mono>{one.name}</Mono>
                </div>
                <span>{one.value}</span>
              </div>
            ))}
          </Stack>
        )}
        {row.effects.length === 0 ? null : (
          <Stack gap={1}>
            {row.effects.map((effect) => (
              <EffectItem key={effect.source} effect={effect} now={now} />
            ))}
          </Stack>
        )}
        {row.tags.length === 0 ? null : (
          <div className="cx-chips">
            {row.tags.map((tag) => (
              <span className="cx-chip" key={tag}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </Stack>
    ),
  };
}

function Held({ readings }: { readonly readings: readonly MountReading[] }) {
  const [filter, setFilter] = useState<StateFilter>({ prefix: "", type: "", tag: "" });
  const now = useNow();

  const rows = stateRows(
    readings.map((one) => ({ mountId: one.mountId, name: one.name, entities: one.entities })),
  );
  const shown = rows.filter((one) => matches(one, filter));
  const groups = byAddress(shown);

  const failed = readings.filter((one) => one.error !== null);
  const waiting = readings.filter((one) => one.waiting !== null);
  const unread = readings.filter((one) => !one.read && one.error === null && one.waiting === null);

  return (
    <Stack gap={4}>
      {failed.map((one) => (
        <StatusBadge key={one.mountId} tone="danger">
          {`${one.name}: ${one.error ?? ""}`}
        </StatusBadge>
      ))}
      {waiting.map((one) => (
        <StatusBadge key={one.mountId} tone="info">
          {`${one.name}: queued at ${String(one.waiting ?? 0)}`}
        </StatusBadge>
      ))}
      {unread.length > 0 ? <Muted small>{`${String(unread.length)} not read yet`}</Muted> : null}

      <Field label="Address begins with">
        <Input
          value={filter.prefix}
          mono
          placeholder="subject.facet.path"
          onChange={(event) => {
            setFilter((held) => ({ ...held, prefix: event.target.value }));
          }}
        />
      </Field>
      <Field label="Type">
        <Select
          value={filter.type}
          options={[
            { value: "", label: "any type" },
            ...typesIn(rows).map((one) => ({ value: one, label: one })),
          ]}
          onChange={(event) => {
            setFilter((held) => ({ ...held, type: event.target.value }));
          }}
        />
      </Field>
      <Field label="Tag">
        <Select
          value={filter.tag}
          options={[
            { value: "", label: "any tag" },
            ...tagsIn(rows).map((one) => ({ value: one, label: one })),
          ]}
          onChange={(event) => {
            setFilter((held) => ({ ...held, tag: event.target.value }));
          }}
        />
      </Field>

      {groups.length === 0 ? (
        <Nothing headline={rows.length === 0 ? "Nothing held" : "No match"} />
      ) : (
        groups.map((group) => (
          <Section key={group.address} title={group.address === "" ? "unaddressed" : group.address}>
            <Stack gap={2}>
              {group.address === "" ? null : (
                <CopyField label="address" value={group.address} />
              )}
              <Accordion multiple items={group.rows.map((row) => entityItem(row, now))} />
            </Stack>
          </Section>
        ))
      )}
    </Stack>
  );
}

export interface StatePanelProps {
  readonly activities: SpaceActivities;
  /** Read every mount through this canvas, the same one the stage is drawing. Granted only to
   * the space's owner; a moderator's read degrades to whatever their own role would see. */
  readonly canvasKind: string;
}

export function StatePanel({ activities, canvasKind }: StatePanelProps) {
  const live = activities.rows.filter((one) => one.mount?.mount_id != null);

  if (live.length === 0) {
    return <Nothing headline="Nothing running" />;
  }

  return (
    <LiveMounts rows={live} asCanvas={canvasKind}>
      {(readings) => <Held readings={readings} />}
    </LiveMounts>
  );
}
